import {
  LRS_DOCUMENT_TYPE,
  NOT_FAILED_FILE_STATUSES,
} from '@packages/domain/constants/documents';
import type { Document } from '@packages/domain/models/document';
import type { RootState } from 'reducers/types';
import {
  isCancelled,
  isDelivered,
  isReceived,
} from 'selectors/entities/payment/payment';

const AVAILABLE_FILE_STATUS = 'available';
const VOID_FILE_STATUS = 'void';
const DOC_VERIFY_WITH_ERRORS_STATUS = 'doc_verify_with_errors';
const DOC_VERIFY_IN_PROGRESS = 'doc_verify_in_progress';
const AVAILABLE_STATUSES = [
  AVAILABLE_FILE_STATUS,
  VOID_FILE_STATUS,
  DOC_VERIFY_WITH_ERRORS_STATUS,
];

export const documentsCollection = (state: RootState) =>
  state?.entities?.documentsCollection ??
  ({} as RootState['entities']['documentsCollection']);

export const selectCollectionToken = (state: RootState) =>
  documentsCollection(state)?.token;

export const isDocVerifyActive = (state: RootState) => {
  const {
    entities: {
      documentsCollection: { docVerifyActive = false },
    },
  } = state;

  return docVerifyActive;
};

export const hasDocumentsCollection = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  return Object.keys(documentsCollection).length > 0;
};

export const hasLRSInDocumentCollection = (state: RootState) => {
  if (isCancelled(state)) {
    return false;
  }

  const {
    entities: { documentsCollection },
  } = state;

  return documentsCollection?.documents?.some(
    d => d.type === LRS_DOCUMENT_TYPE,
  );
};

export const allRequiredFilesUploaded = (state: RootState) => {
  const hasValidFiles = (document: Document) => {
    const { files } = document;

    return files.some(file => NOT_FAILED_FILE_STATUSES.includes(file.status));
  };

  const { documents } = documentsCollection(state);
  const requiredDocuments = documents?.filter(document => document.required);

  return requiredDocuments?.every(document => hasValidFiles(document));
};

export const requiresDocuments = (state: RootState) => {
  if (isReceived(state) || isDelivered(state) || isCancelled(state)) {
    return false;
  }

  return hasDocumentsCollection(state);
};

export const allFilesAnalyzed = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  const documents = documentsCollection.documents;

  if (!documents) {
    return true;
  }

  const allFilesAvailable = documents.some(document => {
    const { files } = document;
    return files.some(file => !AVAILABLE_STATUSES.includes(file.status));
  });

  return !allFilesAvailable;
};

export const selectVerifiedFiles = (
  state: RootState,
): Array<{ documentId: string; fileId: string }> => {
  const documents = state?.entities?.documentsCollection?.documents;
  if (!Array.isArray(documents)) {
    return [];
  }

  return documents.flatMap(document =>
    (document.files ?? [])
      .filter(file =>
        [AVAILABLE_FILE_STATUS, DOC_VERIFY_WITH_ERRORS_STATUS].includes(
          file.status,
        ),
      )
      .map(file => ({ documentId: document.id, fileId: file.id })),
  );
};

export const hasFilesInDocVerifyProcess = (state: RootState) => {
  const {
    entities: { documentsCollection },
  } = state;

  const documents = documentsCollection?.documents;

  if (!Array.isArray(documents)) {
    return false;
  }

  return documents.some(document => {
    const { files } = document;
    return files.some(file => file.status === DOC_VERIFY_IN_PROGRESS);
  });
};
